import { h, Component } from 'preact';
import util from 'preact-util';
import { observer } from 'mobx-preact';
import { Text } from 'preact-i18n';

import { presetDate, startOfDay, PRESET_KEYS } from '../../lib/laterPresets';

const PRESET_LABELS = {
    tomorrow: <Text id='support.later.tomorrow'>Tomorrow</Text>,
    in3days: <Text id='support.later.in3days'>In 3 days</Text>,
    nextWeek: <Text id='support.later.nextWeek'>Next week</Text>,
    in1month: <Text id='support.later.in1month'>In 1 month</Text>,
};

// Format a Date as yyyy-mm-dd for a native date input value.
const toInputValue = (date) => {
    if (!date) return '';
    const d = new Date(date);
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${d.getFullYear()}-${m}-${day}`;
};

@observer
class LaterDateDrawer extends Component {
    constructor(props) {
        super(props);
        const { ticket = {} } = props;
        this.state = {
            // 'tomorrow' | 'in3days' | 'nextWeek' | 'in1month' | 'custom' | 'none' | null
            selection: ticket.laterDate ? 'custom' : null,
            customValue: ticket.laterDate ? toInputValue(ticket.laterDate) : '',
            saving: false,
        };
    }

    closeDrawer = () => {
        const { appState } = this.props.stores;
        const { drawerLevel } = this.props;
        appState.toggleDrawer(false, drawerLevel);
    }

    selectPreset = (key) => {
        this.setState({ selection: key });
    }

    selectNone = () => {
        this.setState({ selection: 'none', customValue: '' });
    }

    onCustomChange = (e) => {
        this.setState({ selection: 'custom', customValue: e.target.value });
    }

    resolveDate = () => {
        const { selection, customValue } = this.state;
        if (selection === 'none' || selection === null) return null;
        if (selection === 'custom') {
            if (!customValue) return null;
            // Parse yyyy-mm-dd as a local start-of-day date.
            const [y, m, d] = customValue.split('-').map(n => parseInt(n, 10));
            return startOfDay(new Date(y, m - 1, d));
        }
        return presetDate(selection);
    }

    handleSet = async () => {
        const { stores, ticket, onSaved } = this.props;
        const { supportTicketStore } = stores;
        const laterDate = this.resolveDate();
        this.setState({ saving: true });
        try {
            await supportTicketStore.setLater(ticket.uuidv4, laterDate ? laterDate.toISOString() : null);
            await supportTicketStore.getUnreadCount();
            if (typeof onSaved === 'function') {
                await onSaved();
            }
        } finally {
            this.setState({ saving: false });
            this.closeDrawer();
        }
    }

    render() {
        const { selection, customValue, saving } = this.state;
        const canSet = selection !== null; // a choice (incl. "no date") has been made

        return (
            <div class="d-flex flex-column gap-3 p-4 mb-5">
                <h5 class="mb-1">
                    <i class="fas fa-calendar-alt me-2" style="color: #8b5cf6;" />
                    <Text id='support.later.title'>Send to Later — pick a deadline</Text>
                </h5>
                <p class="text-body-secondary small mb-2">
                    <Text id='support.later.help'>The ticket reappears in the Open list on this date so you do not forget.</Text>
                </p>

                <div class="d-flex flex-wrap gap-2">
                    {PRESET_KEYS.map(key => (
                        <button
                            key={key}
                            type="button"
                            class={`btn btn-sm ${selection === key ? 'btn-primary' : 'btn-outline-primary'}`}
                            onClick={() => this.selectPreset(key)}
                        >
                            {PRESET_LABELS[key]}
                        </button>
                    ))}
                </div>

                <div>
                    <label class="form-label small mb-1">
                        <Text id='support.later.custom'>Custom date</Text>
                    </label>
                    <input
                        type="date"
                        class="form-control"
                        value={customValue}
                        onInput={this.onCustomChange}
                    />
                </div>

                <div>
                    <button
                        type="button"
                        class={`btn btn-sm ${selection === 'none' ? 'btn-secondary' : 'btn-outline-secondary'}`}
                        onClick={this.selectNone}
                    >
                        <i class="fas fa-ban me-1" />
                        <Text id='support.later.noDate'>No date (just defer)</Text>
                    </button>
                </div>

                <div class="d-flex justify-content-end gap-2 mt-2">
                    <button type="button" class="btn btn-outline-secondary" onClick={this.closeDrawer} disabled={saving}>
                        <Text id='support.later.cancel'>Cancel</Text>
                    </button>
                    <button type="button" class="btn btn-primary" onClick={this.handleSet} disabled={!canSet || saving}>
                        {saving
                            ? <><i class="fas fa-spinner fa-spin me-1" /><Text id='support.later.saving'>Saving...</Text></>
                            : <Text id='support.later.set'>Set</Text>}
                    </button>
                </div>
            </div>
        );
    }
}

export default LaterDateDrawer;
